// components/PrizesSection.js - FIXED VERSION
// Fixed to use the custom total odds handler from parent
import React from 'react';
import Section from './Section';
import './PrizesSection.css'; // Import the dedicated CSS file

export default function PrizesSection({ 
  prizes, 
  setPrizes, 
  totalOdds, 
  setTotalOdds,  // This should be the custom handler from parent
  isEditing, 
  onPrizeChange,  // Accept custom prize change handler from parent
  onImportRequest, // NEW: callback to open import modal
}) {
  const prizeOptions = [
    '$20,000 Instant Cash',
    '$10,000 Instant Cash',
    '$5,000 Instant Cash',
    '$2,500 Instant Cash',
    '$1,000 Instant Cash',
    '85" Sony 4K Smart TV',
    '75" LG 4K Smart TV',
    '36" Blackstone Griddle',
    'Traeger Pro Series Grill',
    'Large Green Egg Grill',
    'up to $550 Walmart Gift Card',
    'up to $550 Instant Cash',
    'up to One Year of Free Chick-Fil-A',
    'up to One Year of Free Whataburger',
    'Wireless Earpods',
    'Smart Watch',
    'LED Smart Clock w/ Charging Dock',
    '$250 Instant Cash',
    '$250 Amazon Gift Card',
    '$250 Walmart Gift Card',
    'Other'
  ];

  // Helper function to format numbers with commas
  const formatNumberWithCommas = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to format value with $ prefix
  const formatValue = (value) => {
    if (!value) return '';
    // Remove any existing $ signs and non-numeric characters except commas and periods
    const cleanValue = value.replace(/[^\d.,]/g, '');
    // If the cleaned value is empty, return empty
    if (!cleanValue) return '';
    // Add $ prefix if not already present
    return value.startsWith('$') ? value : '$' + value;
  };

  // Helper function to format odds with proper comma placement
  const formatOdds = (odds) => {
    if (!odds) return '';
    
    // Handle odds in format "1:50000" or "50000:50000"
    if (odds.includes(':')) {
      const [left, right] = odds.split(':');
      const formattedLeft = left.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const formattedRight = right.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return formattedLeft + ':' + formattedRight;
    }
    
    // Handle plain numbers
    return odds.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Use custom prize change handler if provided, otherwise use default
  const handlePrizeChange = onPrizeChange || ((index, field, value) => {
    const updated = [...prizes];
    
    // Apply formatting for specific fields
    if (field === 'value') {
      value = formatValue(value);
    } else if (field === 'odds') {
      value = formatOdds(value);
    }
    
    updated[index][field] = value;

    // Default auto-generation logic (will be overridden by parent if onPrizeChange is provided)
    if (field === 'prize') {
      switch (index) {
        case 0:
        case 1:
        case 2:
          updated[index].odds = '1:' + formatNumberWithCommas(totalOdds);
          break;
        case 3:
          updated[index].odds = formatNumberWithCommas(totalOdds - 18) + ':' + formatNumberWithCommas(totalOdds);
          break;
        case 4:
          updated[index].odds = '15:' + formatNumberWithCommas(totalOdds);
          break;
        default:
          break;
      }
    }

    setPrizes(updated);
  });

  const handleValueBlur = (index, value) => {
    // Format the value when user finishes editing
    const formatted = formatValue(value);
    if (formatted !== value) {
      handlePrizeChange(index, 'value', formatted);
    }
  };

  const handleOddsBlur = (index, value) => {
    // Format the odds when user finishes editing
    const formatted = formatOdds(value);
    if (formatted !== value) {
      handlePrizeChange(index, 'odds', formatted);
    }
  };

  return (
    <Section title="Prizes" defaultOpen={false}>
      <div className="prizes-container">
        {/* Total Odds Section */}
        <div className="prizes-total-odds" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="prizes-total-odds-field">
            <input
              type="number"
              disabled={!isEditing}
              value={totalOdds}
              onChange={(e) => setTotalOdds(Number(e.target.value))} // FIXED: This now uses the custom handler from parent
              placeholder="50000"
              className="prizes-total-odds-input"
            />
            <div className="prizes-total-odds-label">Total Odds</div>
          </div>
          {onImportRequest && (
            <button
              type="button"
              onClick={() => onImportRequest('prizes')}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(139, 92, 246, 0.25)',
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
              }}
            >
              ↓ Import Prizes
            </button>
          )}
        </div>

        {/* Prizes Table */}
        <div className="prizes-table-container">
          <table className="prizes-table">
            <thead className="prizes-table-header">
              <tr>
                <th className="prizes-col-number">Prize #</th>
                <th className="prizes-col-type">Prize Type</th>
                <th className="prizes-col-odds">Odds</th>
                <th className="prizes-col-value">Value</th>
                <th className="prizes-col-custom">Custom Prize</th>
              </tr>
            </thead>
            <tbody className="prizes-table-body">
              {prizes.map((item, idx) => (
                <tr key={idx} className="prizes-table-row">
                  {/* Prize Number */}
                  <td className="prizes-table-cell prizes-number-cell">
                    <div className="prizes-number-badge">{idx + 1}</div>
                  </td>
                  
                  {/* Prize Type */}
                  <td className="prizes-table-cell">
                    <select
                      className="prizes-select"
                      disabled={!isEditing}
                      value={item.prize}
                      onChange={(e) => handlePrizeChange(idx, 'prize', e.target.value)}
                    >
                      <option value="">Select Prize</option>
                      {prizeOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                  
                  {/* Odds */}
                  <td className="prizes-table-cell">
                    <input
                      className="prizes-input"
                      disabled={!isEditing}
                      value={item.odds}
                      onChange={(e) => handlePrizeChange(idx, 'odds', e.target.value)}
                      onBlur={(e) => handleOddsBlur(idx, e.target.value)}
                      placeholder="1:50,000"
                    />
                  </td>
                  
                  {/* Value */}
                  <td className="prizes-table-cell">
                    <input
                      className="prizes-input"
                      disabled={!isEditing}
                      value={item.value}
                      onChange={(e) => handlePrizeChange(idx, 'value', e.target.value)}
                      onBlur={(e) => handleValueBlur(idx, e.target.value)}
                      placeholder="$2,500"
                    />
                  </td>
                  
                  {/* Custom Prize */}
                  <td className="prizes-table-cell">
                    {item.prize === 'Other' ? (
                      <input
                        type="text"
                        disabled={!isEditing}
                        value={item.custom}
                        onChange={(e) => handlePrizeChange(idx, 'custom', e.target.value)}
                        placeholder="Enter custom prize"
                        className="prizes-custom-input"
                      />
                    ) : (
                      <div className="prizes-na-cell">N/A</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}